/**
 * Error State Component
 * 
 * Displayed when data fetching fails.
 * Provides retry functionality and clear messaging.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ErrorStateProps {
  /** Error message to display */
  message?: string;
  /** Callback when retry button is clicked */
  onRetry?: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({
  message = 'Something went wrong while loading.',
  onRetry,
}) => {
  const { t } = useTranslation('dashboard');

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="text-center py-16"
      role="alert"
    >
      {/* Icon */}
      <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertCircle className="w-10 h-10 text-primary-500" />
      </div>

      {/* Title */}
      <h2 className="text-xl font-semibold text-haat-dark mb-2">
        {t('error.title')}
      </h2>

      {/* Message */}
      <p className="text-haat-gray mb-6 max-w-md mx-auto">
        {message}
      </p>

      {/* Retry Button */}
      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-6 py-3 bg-haat-red text-white font-medium rounded-lg hover:bg-haat-red/90 transition-colors focus:outline-none focus:ring-2 focus:ring-haat-red focus:ring-offset-2"
        >
          <RefreshCw className="w-4 h-4" />
          {t('error.retry')}
        </button>
      )}
    </motion.div>
  );
};

export default ErrorState;

